import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  Drawer as MaterialDrawer,
  DrawerProps as MaterialDrawerProps,
  Grid,
  IconButton,
  Theme,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Close from '@mui/icons-material/Close';
import classNames from 'classnames';
import { DRAWER_Z_INDEX } from '../../../definitions/constants';
import { multilineEllipsis } from '../../styles/mixins';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    overflowX: 'hidden',
    overflowY: 'auto',
    position: 'relative',
  },
  fullHeight: {
    height: '100%',
  },
  multilineTitle: {
    ...multilineEllipsis(2),
  },
  paper: {
    overflowX: 'hidden',
    width: '40%',
    [theme.breakpoints.down('lg')]: {
      width: '50%',
    },
    [theme.breakpoints.down('md')]: {
      width: '60%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  paddedSection: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
  },
  scrollBottom: {
    boxShadow: '0px 4px 5px 0px rgb(0 0 0 / 14%)',
    zIndex: 1,
  },
}));

type ClassKey = 'backButton' | 'paddedSection';

interface Props extends Omit<MaterialDrawerProps, 'classes' | 'title'> {
  actions?: React.ReactNode;
  backButtonLabel?: React.ReactNode;
  classes?: Partial<Record<ClassKey, string>>;
  contentRef?: React.RefObject<HTMLDivElement>;
  multilineTitle?: boolean;
  onBack?: () => void;
  onClose: () => void;
  paddingBottom?: any;
  paddingTop?: any;
  showBackButton?: boolean;
  showCloseButton?: boolean;
  stickyHeader?: React.ReactNode;
  subtitle?:
    | ((isContentScrolling: boolean) => React.ReactNode)
    | React.ReactNode;
  title?: React.ReactNode;
  titleAction?: React.ReactNode;
  titlePaddingBottom?: BoxProps['pb'];
  titleDataTestId?: string;
}

const Drawer: React.FC<Props> = (props: Props): JSX.Element => {
  const classes = useStyles();

  const {
    actions,
    backButtonLabel,
    children,
    classes: classesProp,
    contentRef,
    multilineTitle = false,
    onBack,
    onClose,
    open,
    paddingBottom = { xs: 2, md: 5 },
    paddingTop = { xs: 2, md: 5 },
    showBackButton = false,
    showCloseButton = false,
    stickyHeader,
    subtitle,
    title,
    titleAction,
    titlePaddingBottom = 2,
    titleDataTestId,
    ...drawerProps
  } = props;

  const isContentScrolling = useScrollTrigger({
    disableHysteresis: true,
    target: contentRef?.current ? (contentRef?.current as Node) : undefined,
    threshold: 0,
  });

  /**
   * Dirty approach to force a re-render in order to have the contentRef available
   * for the drawer's scroll trigger to work properly. This happens because
   * the content is not rendered until the drawer is open, so the contentRef
   * prop will remain null when the scrollTrigger is initialized.
   */
  const [, setLastOpen] = useState(0);

  useEffect(() => {
    // ugly timeout to force a re-render in order
    // for the content scroll trigger to work properly
    const t = setTimeout(() => {
      setLastOpen(Date.now());
    }, 50);

    return () => {
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const renderBackButton = (): React.ReactNode => {
    if (!showBackButton) {
      return undefined;
    }

    if (backButtonLabel) {
      return (
        <Grid item xs zeroMinWidth>
          <Button
            className={classesProp?.backButton}
            color="inherit"
            onClick={onBack}
            startIcon={
              <ArrowBack
                sx={{ color: 'text.secondary', fontSize: '1.5rem !important' }}
              />
            }
            sx={{ ml: -0.5, textTransform: 'none' }}
          >
            {backButtonLabel}
          </Button>
        </Grid>
      );
    }

    return (
      <IconButton
        className={classesProp?.backButton}
        edge="start"
        onClick={onBack}
        size="large"
      >
        <ArrowBack />
      </IconButton>
    );
  };

  const renderTitle = (): React.ReactNode => {
    if (typeof title === 'string') {
      return (
        <Grid item xs zeroMinWidth>
          <Typography
            className={classNames({ [classes.multilineTitle]: multilineTitle })}
            color="textPrimary"
            noWrap={!multilineTitle}
            title={title}
            variant="h5"
            data-testid={titleDataTestId}
          >
            {title}
          </Typography>
        </Grid>
      );
    }

    return (
      <Grid item xs zeroMinWidth>
        {title}
      </Grid>
    );
  };

  const renderTitleAction = (): React.ReactNode => {
    if (showCloseButton) {
      return (
        <IconButton edge="end" onClick={onClose} size="large">
          <Close />
        </IconButton>
      );
    }

    return titleAction;
  };

  return (
    <MaterialDrawer
      {...drawerProps}
      classes={{ paper: classes.paper }}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      open={open}
      style={{ zIndex: DRAWER_Z_INDEX }}
    >
      {stickyHeader}
      <Box className={classes.fullHeight} pb={paddingBottom} pt={paddingTop}>
        <Grid
          className={classes.fullHeight}
          container
          direction="column"
          wrap="nowrap"
        >
          <Box
            className={classNames(
              classes.paddedSection,
              classesProp?.paddedSection,
              {
                [classes.scrollBottom]: isContentScrolling,
              }
            )}
            pb={subtitle ? undefined : titlePaddingBottom}
          >
            <Grid alignItems="center" container>
              {renderBackButton()}
              {renderTitle()}
              {renderTitleAction()}
            </Grid>
            {subtitle && (
              <Box pt={1} pb={3}>
                {typeof subtitle === 'function'
                  ? subtitle(isContentScrolling)
                  : subtitle}
              </Box>
            )}
          </Box>

          <Grid
            ref={contentRef}
            className={classNames(
              classes.paddedSection,
              classesProp?.paddedSection,
              classes.content
            )}
            item
            xs
          >
            {children}
          </Grid>

          {actions && (
            <Box pt={3}>
              <Grid
                className={classNames(
                  classes.paddedSection,
                  classesProp?.paddedSection
                )}
                container
                item
                justifyContent="flex-end"
              >
                {actions}
              </Grid>
            </Box>
          )}
        </Grid>
      </Box>
    </MaterialDrawer>
  );
};

export default Drawer;
